/**
 * BookingPage
 * Main orchestrator for the booking wizard
 *
 * This page has been refactored from a 1476-line monolith into:
 * - Shared types (types.ts)
 * - useAddressAutocomplete hook for Nominatim address lookup
 * - useBookingForm hook for form state, validation, submission
 * - Step components (ServicePackageStep, DateTimeStep, ContactDetailsStep)
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FiAlertCircle } from "react-icons/fi";
import SEO from "@/components/SEO";
import { seoConfig } from "@/config/seo.config";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";
import BookingStepsIndicator from "@/components/booking/BookingStepsIndicator";
import PackageComparison from "@/components/booking/PackageComparison";
import { SessionTimeoutWarning } from "@/components/ui/SessionTimeoutWarning";
import { useBookingSettingsStore } from "@/store/bookingSettingsStore";
import { useCartStore } from "@/store/cartStore";
import { useAuthStore } from "@/store/authStore";
import { usePackageStore } from "@/store/packageStore";
import { resendVerificationEmail } from "@/services/auth.service";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import { sanitizeString, sanitizeEmail, sanitizePhone } from "@/utils/sanitize";
import { formatCurrency } from "@/utils/format";
import { ROUTES } from "@/config/constants";
import { BookingFormData } from "@/types";
import { AddressSuggestion } from "./types";

// Local imports
import { useAddressAutocomplete } from "./useAddressAutocomplete";
import { useBookingForm } from "./useBookingForm";
import { ServicePackageStep, DateTimeStep, ContactDetailsStep } from "./steps";

export default function BookingPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { settings } = useBookingSettingsStore();
  const addItem = useCartStore((state) => state.addItem);
  const setBookingData = useCartStore((state) => state.setBookingData);
  const clearCart = useCartStore((state) => state.clearCart);
  const services = usePackageStore((state) => state.services);
  const addons = usePackageStore((state) => state.addons);

  // Session timeout monitoring for authenticated users
  const {
    showWarning: showSessionWarning,
    minutesRemaining: sessionMinutesRemaining,
    isRefreshing: isRefreshingSession,
    refreshSession,
    dismissWarning: dismissSessionWarning,
  } = useSessionTimeout();

  // Address autocomplete
  const suggestionClickedRef = useRef(false);
  const {
    addressQuery,
    setAddressQuery,
    addressSuggestions,
    addressLoading,
    showSuggestions,
    setShowSuggestions,
    locatingAddress,
    selectAddress,
    handleUseMyLocation,
  } = useAddressAutocomplete();

  // Form state and validation
  const {
    step,
    setStep,
    formData,
    setFormData,
    errors,
    setErrors,
    touched,
    setTouched,
    loading,
    setLoading,
    availableTimeSlots,
    checkingAvailability,
    selectedPackage,
    setSelectedPackage,
    selectedAddons,
    subtotalAmount,
    totalAmount,
    validateStep,
    handleFieldBlur,
    handlePhoneChange,
  } = useBookingForm({ settings, addons });

  // Package builder enhancements
  const [showComparison, setShowComparison] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  // Memoized service sorting by price
  const minServicePriceById = useMemo(() => {
    const map = new Map<string, number>();
    if (!settings?.packages) return map;

    for (const pkg of settings.packages) {
      if (!pkg.enabled) continue;
      const current = map.get(pkg.serviceId);
      if (current === undefined || pkg.price < current) {
        map.set(pkg.serviceId, pkg.price);
      }
    }

    return map;
  }, [settings?.packages]);

  const servicesSortedByPrice = useMemo(() => {
    const decorated = services.map((service) => ({
      service,
      minPrice: minServicePriceById.get(service.id),
    }));

    decorated.sort((a, b) => {
      const aPrice = a.minPrice ?? Number.POSITIVE_INFINITY;
      const bPrice = b.minPrice ?? Number.POSITIVE_INFINITY;
      if (aPrice !== bPrice) return aPrice - bPrice;
      return a.service.order - b.service.order;
    });

    return decorated.map((x) => x.service);
  }, [services, minServicePriceById]);

  // Clear cart on mount
  useEffect(() => {
    clearCart();
  }, [clearCart]);

  // Load package when selected
  useEffect(() => {
    if (formData.packageId && settings?.packages) {
      const pkg = settings.packages.find((p) => p.id === formData.packageId);
      setSelectedPackage(pkg || null);
    }
  }, [formData.packageId, settings, setSelectedPackage]);

  // Pre-fill form with user data
  useEffect(() => {
    if (user && step === 3) {
      setFormData((prev) => ({
        ...prev,
        customerName: prev.customerName || user.displayName,
        customerEmail: prev.customerEmail || user.email,
        customerPhone: prev.customerPhone || user.phoneNumber || "",
      }));
    }
  }, [user, step, setFormData]);

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      // Mark all step 3 fields as touched to show errors
      setTouched({
        customerName: true,
        customerEmail: true,
        customerPhone: true,
        propertyAddress: true,
        propertyType: true,
      });

      // Scroll to top to show error banner
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setShowConfirmDialog(true);
  };

  const confirmSubmit = async () => {
    setShowConfirmDialog(false);
    setLoading(true);

    try {
      // Sanitize form data before submission
      const sanitizedFormData: BookingFormData = {
        ...formData,
        customerName: sanitizeString(formData.customerName),
        customerEmail: sanitizeEmail(formData.customerEmail),
        customerPhone: sanitizePhone(formData.customerPhone),
        propertyAddress: sanitizeString(formData.propertyAddress),
        propertyType: sanitizeString(formData.propertyType),
        specialInstructions: formData.specialInstructions
          ? sanitizeString(formData.specialInstructions)
          : undefined,
      } as BookingFormData;

      if (selectedPackage) {
        addItem({
          type: "package",
          name: selectedPackage.name,
          details: selectedPackage.details,
          price: selectedPackage.price,
          quantity: 1,
        });
      }

      selectedAddons.forEach((addon) => {
        addItem({
          type: "addon",
          name: addon.title,
          details: addon.description,
          price: addon.price,
          quantity: 1,
        });
      });

      setBookingData(sanitizedFormData);
      navigate(ROUTES.CART);
    } catch (error) {
      console.error("Error creating booking:", error);
      alert(
        "Unable to add booking to cart. This may be due to a network error or the selected time slot is no longer available. Please refresh the page and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!user?.email) return;

    setResendingVerification(true);
    try {
      await resendVerificationEmail(user.email);
      setVerificationSent(true);
      setTimeout(() => setVerificationSent(false), 5000);
    } catch (error) {
      console.error("Error resending verification email:", error);
      alert("Failed to resend verification email. Please try again.");
    } finally {
      setResendingVerification(false);
    }
  };

  // Handle address selection from autocomplete
  const handleAddressSelect = (suggestion: AddressSuggestion) => {
    suggestionClickedRef.current = true;
    selectAddress(suggestion);
    setFormData((prev) => ({
      ...prev,
      propertyAddress: suggestion.description,
    }));
    setTimeout(() => {
      suggestionClickedRef.current = false;
    }, 300);
  };

  // Handle "Use My Location"
  const handleLocationRequest = async () => {
    const address = await handleUseMyLocation();
    if (address) {
      setFormData((prev) => ({
        ...prev,
        propertyAddress: address,
      }));
    }
  };

  // Check if user's email is verified
  const isEmailVerified = !user || user.email_confirmed_at;

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-900 py-8 sm:py-12">
      <SEO {...seoConfig.booking} />

      {/* Session Timeout Warning */}
      {user && showSessionWarning && sessionMinutesRemaining !== null && (
        <SessionTimeoutWarning
          minutesRemaining={sessionMinutesRemaining}
          isRefreshing={isRefreshingSession}
          onRefresh={refreshSession}
          onDismiss={dismissSessionWarning}
        />
      )}

      <div className="section-container">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 dark:text-white mb-2">
              Book a Service
            </h1>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-300">
              Complete the form to schedule your appointment
            </p>
          </div>

          {/* Email Verification Warning */}
          {user && !isEmailVerified && (
            <div className="mb-6">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FiAlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-1">
                      Email Verification Required
                    </h3>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
                      Please verify your email address before making a booking. We've sent a
                      verification link to <span className="font-medium">{user.email}</span>.
                    </p>
                    {verificationSent && (
                      <p className="text-sm text-green-700 dark:text-green-300 mb-2">
                        ✓ Verification email sent! Please check your inbox.
                      </p>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleResendVerification}
                      loading={resendingVerification}
                      className="border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                    >
                      {resendingVerification ? "Sending..." : "Resend Verification Email"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Progress Steps */}
          <BookingStepsIndicator currentStep={step} />

          <Card>
            <div className="p-6 sm:p-8">
              {/* Step 1: Select Service & Package */}
              {step === 1 && (
                <ServicePackageStep
                  formData={formData}
                  errors={errors}
                  services={servicesSortedByPrice}
                  packages={settings?.packages || []}
                  addons={addons}
                  selectedPackage={selectedPackage}
                  selectedAddons={selectedAddons}
                  subtotalAmount={subtotalAmount}
                  totalAmount={totalAmount}
                  minServicePriceById={minServicePriceById}
                  onFormChange={(updates) => setFormData((prev) => ({ ...prev, ...updates }))}
                  onAddonToggle={(addonId) => {
                    setFormData((prev) => {
                      const current = prev.addonIds || [];
                      const updated = current.includes(addonId)
                        ? current.filter((id) => id !== addonId)
                        : [...current, addonId];
                      return { ...prev, addonIds: updated };
                    });
                  }}
                  onNext={handleNext}
                />
              )}

              {/* Step 2: Select Date & Time */}
              {step === 2 && (
                <DateTimeStep
                  formData={formData}
                  errors={errors}
                  settings={settings}
                  availableTimeSlots={availableTimeSlots}
                  checkingAvailability={checkingAvailability}
                  onFormChange={(updates) => {
                    setFormData((prev) => ({ ...prev, ...updates }));
                    // Clear date error when date is selected
                    if (updates.scheduledDate) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.scheduledDate;
                        return newErrors;
                      });
                    }
                  }}
                  onNext={handleNext}
                  onBack={handleBack}
                />
              )}

              {/* Step 3: Contact & Property Details */}
              {step === 3 && (
                <ContactDetailsStep
                  formData={formData}
                  errors={errors}
                  touched={touched}
                  addressQuery={addressQuery}
                  addressSuggestions={addressSuggestions}
                  showAddressSuggestions={showSuggestions}
                  addressLoading={addressLoading}
                  locatingAddress={locatingAddress}
                  suggestionClickedRef={suggestionClickedRef}
                  onFormChange={(updates) => setFormData((prev) => ({ ...prev, ...updates }))}
                  onFieldBlur={handleFieldBlur}
                  onPhoneChange={handlePhoneChange}
                  onAddressQueryChange={(query) => {
                    setAddressQuery(query);
                    if (query.length >= 3) {
                      setShowSuggestions(true);
                    } else {
                      setShowSuggestions(false);
                    }
                  }}
                  onAddressSelect={handleAddressSelect}
                  onAddressFocus={() => {
                    if (addressQuery.length >= 3 && addressSuggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  onAddressBlur={() => setShowSuggestions(false)}
                  onUseMyLocation={handleLocationRequest}
                  onSubmit={handleSubmit}
                  onBack={handleBack}
                  isSubmitting={loading}
                />
              )}
            </div>
          </Card>

          {/* Confirmation Modal */}
          {showConfirmDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-xl max-w-md w-full p-6">
                <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-4">
                  Confirm Booking Details
                </h3>
                <div className="space-y-3 mb-6 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-300">Package:</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {selectedPackage?.name}
                    </span>
                  </div>
                  {selectedAddons.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-neutral-600 dark:text-neutral-300">Add-ons:</span>
                      <span className="font-medium text-neutral-900 dark:text-white">
                        {selectedAddons.length} selected
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-300">Date:</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {formData.scheduledDate
                        ? new Date(formData.scheduledDate).toLocaleDateString()
                        : ""}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-300">Time:</span>
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {formData.scheduledTime}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-600 dark:text-neutral-300">Total:</span>
                    <span className="font-bold text-primary-600">
                      {formatCurrency(totalAmount)}
                    </span>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowConfirmDialog(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button onClick={confirmSubmit} loading={loading} className="flex-1">
                    Confirm & Continue
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Package Comparison Modal */}
          {showComparison && formData.serviceId && settings?.packages && (
            <PackageComparison
              packages={settings.packages.filter(
                (pkg) => pkg.serviceId === formData.serviceId && pkg.enabled
              )}
              addons={addons}
              selectedPackageId={formData.packageId}
              onSelectPackage={(packageId) => {
                setFormData((prev) => ({ ...prev, packageId }));
              }}
              onClose={() => setShowComparison(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
